// Market today's view (handoff 4.1c, 4.1d): the tiles, the rows on one shared dock-price axis, the
// legend and Show all, worked out from the controller's market data, the measured width and the
// Show all state. The card's template binds these fields and nothing else: it calls no function
// and formats no string, so nothing is worked out again while the page sits idle.
//
// No Angular here, so Node can test it (marketTodayView.test.mjs).
import {
  countLabel, deltaLabel, formatPrice, niceTicks, priceRange, rowSentence, scaleX, visibleRows,
} from './marketToday.js';

/**
 * Whether the card shows the median's change since the previous report ("▲ 1.50¢" under a row's
 * count, the legend's note, "up 1.50 cents" in the spoken row). Off for now, at Dave's word
 * (2026-10-02); marketRows still works the change out.
 */
export const SHOW_CHANGE = false;

/** The card body's side padding. */
export const PAD = 10;
/** Room for the dots at each end of a strip. */
export const STRIP_INSET = 8;
/** The width an axis value needs. */
export const TICK_LABEL_ROOM = 40;

const px = n => `${n}px`;

/**
 * A row's columns for a card body `width` px wide, its side padding included: the egg-type key,
 * the strip, and the counts on the right. The key and the counts take a share of the room within
 * bounds, the strip what is left: 0 while the width isn't measured, and then no strip is drawn.
 */
export function columns(width) {
  const inner = Math.max(0, width - 2 * PAD);
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, Math.round(n)));
  const key = clamp(inner * 0.24, 72, 150);
  const right = clamp(inner * 0.27, 88, 170);
  return {inner, key, right, strip: Math.max(0, inner - key - right)};
}

/** How many values the axis may have: one fewer than fit under the strip, from 2 to 4 (the
 *  app's fontScale is 1 on the web). */
export const maxTicksFor = strip =>
  Math.max(2, Math.min(4, Math.floor((strip - 2 * STRIP_INSET) / TICK_LABEL_ROOM) - 1));

/** The views the tiles switch between: what each draws, and on which price. */
export const VIEWS = ['trades', 'bids', 'offers'];

/**
 * The count tiles, Trades (grey), Bids (the bought blue) and Offers (the sold orange), as buttons
 * that switch the rows below to theirs: `pressed` on the one shown. Each is named by its count as a
 * sentence ("1 bid today") and what pressing it does.
 */
function tiles(today, counts, view) {
  const tile = (key, label, value) => {
    const sentence = `${value} ${(value === 1 ? label.slice(0, -1) : label).toLowerCase()} today`;
    return {key, label, value, cls: `hc-mt-tile-${key}`, pressed: view === key, buttonLabel: `${sentence}, show them`};
  };
  return [
    // today_bot_counts leaves out trades after 4 pm, the board doesn't: its count once it has loaded.
    tile('trades', 'Trades', today ? today.data.length : counts?.Trades ?? 0),
    tile('bids', 'Bids', counts?.Bids ?? 0),
    tile('offers', 'Offers', counts?.Offers ?? 0),
  ];
}

/**
 * A row's marks on its strip, in drawing order: the range (only when the prices differ), the
 * median's invisible hover line, the other members' formula rings and then their dots, the
 * median tick, and the member's own formula rings and dots last, so they sit on top. A formula
 * ring is unfilled and a little larger than a dot, and drawn before the dots: a flat trade at the
 * same price shows inside it, and each keeps its tooltip.
 */
function stripMarks(row, x) {
  const formula = row.formulaPoints || [];
  const own = ownWord(row);
  return {
    range: row.min !== null && row.max > row.min ? {x1: x(row.min), x2: x(row.max)} : null,
    // Web-only: formula trades at their flat prices, hollow, apart from the range and median.
    formulaOthers: formula.filter(p => !p.side).map(p => ({cx: x(p.price), ...readout(p, own)})),
    others: row.points.filter(p => !p.side).map(p => ({cx: x(p.price), ...readout(p, own)})),
    median: row.median === null ? null : {x: x(row.median), value: formatPrice(row.median), detail: 'median'},
    ownFormula: formula.filter(p => p.side).map(p => ({cx: x(p.price), cls: `hc-hollow hc-hollow-${p.side}`, ...readout(p, own)})),
    own: row.points.filter(p => p.side).map(p => ({cx: x(p.price), cls: `hc-${p.side} hc-ring`, ...readout(p, own)})),
  };
}

/** What the member's own dot is called: "you bought" / "you sold" on a trade, "yours" on a bid or offer. */
const ownWord = row => (row.noun && row.noun !== 'trade' ? () => 'yours' : side => `you ${side}`);

/**
 * A dot's hover readout: its price, then what it is, the formula a formula trade was priced by
 * (as the Trades board's Market Adj. column writes it) and whose it is: "44.00", "formula -2.00
 * SE LG · you bought". Null detail for another member's flat trade.
 */
function readout(p, own) {
  const detail = [
    p.region !== undefined ? `${p.market > 0 ? '+' : ''}${p.market.toFixed(2)} ${p.region} ${p.size}`.trim() : '',
    p.side ? own(p.side) : '',
  ].filter(Boolean).join(' · ');
  return {value: formatPrice(p.price), detail: detail || null};
}

/** How far from a mark the pointer may be and still hover it: a 24 px target (dataviz). */
export const HOVER_RADIUS = 12;

/**
 * What the pointer hovers on a strip, at `x` px from its left: the mark nearest it, within
 * `radius`, and every mark drawn within 1 px of that one (a dot at the same price, the median on
 * it). Null when nothing is that close. The readout lines go top mark first (own dots, then the
 * median, then the others'), with like lines counted: {value: '45.00', detail: null, count: 2}.
 * @returns {{x: number, lines: {value: string, detail: ?string, count: number}[]}|null}
 */
export function hoverAt(marks, x, radius = HOVER_RADIUS) {
  if (!marks) {
    return null;
  }
  const all = [
    ...marks.own.map(m => ({x: m.cx, m})),
    ...marks.ownFormula.map(m => ({x: m.cx, m})),
    ...(marks.median ? [{x: marks.median.x, m: marks.median}] : []),
    ...marks.others.map(m => ({x: m.cx, m})),
    ...marks.formulaOthers.map(m => ({x: m.cx, m})),
  ];
  const nearest = all.reduce((best, c) => (Math.abs(c.x - x) < Math.abs(best.x - x) ? c : best), all[0]);
  if (!nearest || Math.abs(nearest.x - x) > radius) {
    return null;
  }
  const lines = [];
  all.filter(c => Math.abs(c.x - nearest.x) < 1).forEach(({m}) => {
    const same = lines.find(l => l.value === m.value && l.detail === m.detail);
    if (same) {
      same.count++;
    } else {
      lines.push({value: m.value, detail: m.detail, count: 1});
    }
  });
  return {x: nearest.x, lines};
}

/**
 * The axis under the rows and the gridlines behind them, for a body laid out as `cols`.
 * Positions are from the rows block's left edge, where a tick sits at `key + x(t)`.
 *
 * A value's box is as wide as the gap between ticks, and narrower near the body's edges, so it
 * stays centred on its tick without spilling out of the card. A value too long for its box wraps,
 * and an invisible copy of the longest value, in the narrowest box, keeps the height that needs.
 */
export function axisView(axis, cols, basis = 'dock') {
  const x = t => scaleX(t, axis, cols.strip, STRIP_INSET);
  const spacing = axis.ticks.length > 1 ? x(axis.ticks[1]) - x(axis.ticks[0]) : cols.strip;
  const ticks = axis.ticks.map(t => {
    const at = cols.key + x(t);
    return {at, text: formatPrice(t), width: Math.max(0, Math.min(spacing, 2 * at, 2 * (cols.inner - at)))};
  });
  // Gridlines and tick marks are 1 px wide, centred on their tick.
  const line = ({at}) => ({left: px(at - 0.5)});
  return {
    gridlines: ticks.map(line),
    ticks: ticks.map(line),
    values: ticks.map(({at, text, width}) => ({text, style: {left: px(at - width / 2), width: px(width)}})),
    reserve: {
      text: ticks.reduce((longest, t) => (t.text.length > longest.length ? t.text : longest), ''),
      style: {width: px(Math.min(...ticks.map(t => t.width)))},
    },
    // The price at the seller's dock, without freight, compares across lanes and pickups. The
    // axis carries no unit: the only cents sign on the card is on the changes.
    title: {text: basis === 'sale' ? 'Sale price' : 'Dock price', style: {marginLeft: px(cols.key), width: px(cols.strip)}},
  };
}

/**
 * The legend for the rows shown. "You bought" and "You sold" appear only when a row shown has a
 * dot of theirs; beside them the other dots are "Others", and alone they are just "Trade" (a staff
 * login, or a day the member hasn't traded at a flat price). On the bids it is "Your bid" and
 * "Bid", on the offers "Your offer" and "Offer". The note explains the ▲▼ changes when one shows.
 */
function legend(shown, previousDay, view) {
  const sides = new Set(shown.flatMap(row => [...row.points, ...(row.formulaPoints || [])].map(p => p.side)));
  const own = sides.has('bought') || sides.has('sold');
  const noun = {trades: 'Trade', bids: 'Bid', offers: 'Offer'}[view];
  return {
    bought: sides.has('bought') ? (view === 'trades' ? 'You bought' : `Your ${noun.toLowerCase()}`) : null,
    sold: sides.has('sold') ? (view === 'trades' ? 'You sold' : `Your ${noun.toLowerCase()}`) : null,
    others: own ? 'Others' : noun,
    formula: shown.some(row => (row.formulaPoints || []).length) ? `${noun} (market-based)` : null,
    median: 'Median',
    note: shown.some(row => row.delta !== null) ? `▲▼ change in the median since ${previousDay}` : null,
  };
}

/** The Show all / Show fewer button: N is every egg type of the day. */
function showAll(count, expanded) {
  return expanded
    ? {text: 'Show fewer', label: 'Show fewer egg types', expanded: true}
    : {text: `Show all ${count}`, label: `Show all ${count} egg types`, expanded: false};
}

/**
 * Everything the Today's activity card binds, in the card's order: the tiles, the empty state, the
 * rows (with their strips and the gridlines behind them), the axis, the legend and Show all.
 *
 * - `market`: the controller's `{today, rows: {sale, dock}, previousDay, bos: {bids, offers}, refused}`.
 *   `today` is null until the board loads, `bos` until today's bids and offers do. `refused` has
 *   the backend's message for a board it won't show this login (no trading access), as
 *   `{trades, bos}`, null for one it shows: that board's views show it in place of rows, and with
 *   both refused there are no tiles to switch between them.
 * - `counts`: today_bot_counts' `{Bids, Offers, Trades}`, undefined until it answers.
 * - `width`: the body's content width as chart-width measures it, undefined until it has.
 * - `expanded`: whether Show all is on.
 * - `view`: the tile pressed, whose rows are drawn: 'trades' (the default), 'bids' or 'offers'.
 * - `basis`: the trades' price, 'sale' (the default) or 'dock'; `market.rows` has rows on both.
 *   Bids are always on the sale price (delivered), offers on the dock price.
 *
 * `axis` (with the gridlines) is null unless the rows shown have prices and the strip has room;
 * `legend` needs only the prices; each row's `marks` are null when no strip is drawn.
 */
export function marketTodayView({market, counts, width, expanded, view = 'trades', basis = 'sale', showChange = SHOW_CHANGE}) {
  const today = market?.today ?? null;
  const bos = market?.bos ?? null;
  if (view === 'bids') {
    basis = 'sale';
  } else if (view === 'offers') {
    basis = 'dock';
  }
  const refused = market?.refused ?? {};
  const refusal = (view === 'trades' ? refused.trades : refused.bos) || null;
  // A refused board's view has its message in place of rows, whatever an earlier load left.
  const rows = refusal ? [] : (view === 'trades' ? market?.rows?.[basis] : bos?.[view]) ?? [];
  const loaded = view === 'trades' ? today : bos;
  // Bids and offers have no previous day to change from.
  showChange = showChange && view === 'trades';
  const previousDay = market?.previousDay;
  // chart-width measures the content box; columns() takes the body with its padding.
  const cols = columns((width || 0) + 2 * PAD);
  const shown = visibleRows(rows, expanded);
  // One axis for the whole card, over the rows shown, so Show all can rescale it.
  const range = priceRange(shown);
  const axis = range ? niceTicks(range[0], range[1], maxTicksFor(cols.strip)) : null;
  const drawn = axis !== null && cols.strip > 0;
  const x = price => scaleX(price, axis, cols.strip, STRIP_INSET);

  return {
    // The card's heading names what the rows below are: it changes with the tile pressed.
    title: `Today's ${view}`,
    tiles: refused.trades && refused.bos ? [] : tiles(today, counts, view),
    empty: refusal || (loaded && !rows.length ? `No ${view} yet today` : null),
    rows: shown.map(row => ({
      key: row.key,
      sentence: rowSentence(showChange ? row : {...row, delta: null}, previousDay),
      count: countLabel(row),
      delta: showChange ? deltaLabel(row) : null,
      marks: drawn ? stripMarks(row, x) : null,
    })),
    columns: {key: {width: px(cols.key)}, strip: {width: px(cols.strip)}, right: {width: px(cols.right)}},
    stripWidth: cols.strip,
    axis: drawn ? axisView(axis, cols, basis) : null,
    // Web-only: which price the trades show. Offered once there are trades to show on it; bids
    // and offers each have only one.
    basis: view === 'trades' && rows.length ? {
      sale: {text: 'Sale', label: 'Show sale prices', pressed: basis === 'sale'},
      dock: {text: 'Dock', label: 'Show dock prices', pressed: basis === 'dock'},
    } : null,
    legend: axis ? legend(showChange ? shown : shown.map(row => ({...row, delta: null})), previousDay, view) : null,
    toggle: rows.length > visibleRows(rows, false).length ? showAll(rows.length, expanded) : null,
  };
}
